import { Image, Table, Tooltip, Typography, message } from "antd"
import React, { useEffect, useState } from "react"
import "./Tape.css"
import { collection, doc, onSnapshot, query, updateDoc, where } from "firebase/firestore"
import moment from "moment"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { DndContext } from "@dnd-kit/core"
import { CSS } from "@dnd-kit/utilities"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { MenuOutlined } from "@ant-design/icons"
import { db } from "../../firebase/config"
import TapeFillOrderDrawer from "./TapeFillOrderDrawer"
import { OrderAndPrefix } from "../../components/Orders/Helpers/OrderAndPrefix"

const { Text } = Typography

const Row = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  })
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && {
        ...transform,
        scaleY: 1,
      },
    ),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
        }
      : {}),
  }
  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if (child.key === "sort") {
          return React.cloneElement(child, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{
                  touchAction: "none",
                  cursor: "move",
                }}
                {...listeners}
              />
            ),
          })
        }
        return child
      })}
    </tr>
  )
}

export default function ActiveTapeOrders() {
  const [tapePrintData, setTapePrintData] = useState([])
  //array that manages order and gets updates
  const [tapeSequence, setTapeSequence] = useState(tapePrintData.map((item) => item.id))
  //loading for while data is sorting
  const [isSorting, setIsSorting] = useState(true)

  const tapePrintingCols = [
    {
      key: "sort",
      width: "3%",
      align: "center",
    },
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      render: (value, record) => OrderAndPrefix(record),
    },

    {
      title: "Priority",
      width: "5%",
      dataIndex: "id",
      align: "center",
      render: (id) => tapePrintData.indexOf(id) + 1,
    },
    {
      title: "Date",
      dataIndex: "created",
      align: "center",
      sorter: (a, b) => a.date.created.seconds - b.date.created.seconds,
      render: (value) => (
        <>
          <Text>{moment.unix(value.seconds).format("DD/MM/YYYY")}</Text>
        </>
      ),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      ellipsis: {
        showTitle: false,
      },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      ellipsis: {
        showTitle: false,
      },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Box Count",
      dataIndex: "boxCount",
      align: "center",
    },
    {
      title: "Roll Amount",
      dataIndex: "rollCount",
      align: "center",
    },
    {
      title: "Roll Size",
      dataIndex: ["selectedProduct", "tapeWidth"],
      align: "center",
    },
    {
      title: "Material",
      dataIndex: ["selectedProduct", "material"],
      align: "center",
      ellipsis: {
        showTitle: false,
      },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Plate",
      dataIndex: ["selectedPlate", "code"],
      align: "center",
      ellipsis: {
        showTitle: false,
      },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "center",
      render: (value) => (
        <>
          <Image src={value} />
        </>
      ),
    },
    {
      title: "Details",
      align: "center",
      render: (value) => <TapeFillOrderDrawer orderData={value} tapeSequence={tapeSequence} />,
    },
  ]

  //gets array of the sequence
  useEffect(() => {
    const sequenceDocRef = doc(db, "sequence", "q1f62HLhOvGSo9ZtmSEb")
    const unsubscribe = onSnapshot(sequenceDocRef, (doc) => {
      if (doc.exists()) {
        const sequenceData = doc.data()
        setTapeSequence(sequenceData.tapeSequence)
        console.log("sequence")
      }
    })
    return () => unsubscribe()
  }, [])

  //fetches the documents, then uses the sequence array to sort in correct order
  useEffect(() => {
    const ref = collection(db, "orders")
    let q = query(
      ref,
      where("orderStatus", "==", 3.5),
      where("selectedProduct.machine", "==", "Tape"),
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        const data = doc.data()
        results.push({ ...data, key: doc.id, id: doc.id })
      })
      // sort the results based on the arsomaSequence state
      const sortedResults = results.sort((a, b) => {
        return tapeSequence.indexOf(a.id) - tapeSequence.indexOf(b.id)
      })
      setTapePrintData(sortedResults)
      setIsSorting(false)

      //   console.log("sorted res", sortedResults);
    })

    return () => unsubscribe()
  }, [tapeSequence])

  //ARSOMA SEQUENCE
  const updateArsomaSequence = async (seq) => {
    const ref = doc(db, "sequence", "q1f62HLhOvGSo9ZtmSEb")
    try {
      await updateDoc(ref, {
        tapeSequence: seq,
      }).then(() => {
        message.success("updated!")
      })
    } catch (error) {
      console.log(error.message)
      message.error(error)
    }
  }

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setTapePrintData((prev) => {
        const activeIndex = prev.findIndex((i) => i.key === active.id)
        const overIndex = prev.findIndex((i) => i.key === over?.id)
        const newArsomaData = arrayMove(prev, activeIndex, overIndex)
        const newIdOrder = newArsomaData.map((item) => item.id)
        updateArsomaSequence(newIdOrder) //updates order online
        return newArsomaData
      })
    }
  }

  return (
    <>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          // rowKey array
          items={tapePrintData.map((i) => i.key)}
          strategy={verticalListSortingStrategy}>
          <Table
            bordered
            size="small"
            className="custom-table"
            rowKey={(record) => record.id}
            dataSource={tapePrintData}
            loading={isSorting}
            columns={tapePrintingCols}
            components={{
              body: {
                row: Row,
              },
            }}
          />
        </SortableContext>
      </DndContext>
    </>
  )
}
